import { inject, Injectable } from "@angular/core";
import { race, take, timer } from "rxjs";
import { CellType, ICellPosition, IgxColumnComponent } from '@infragistics/igniteui-angular/grids/core';
import { IgxGridComponent } from '@infragistics/igniteui-angular/grids/grid';
import
{
    ClassMetadata,
    GridEditType,
    IGridViewMetadata,
    KeyValuePair,
    PropertyMetadata
} from "@tylertech/tyler-api-types";
import { GridMetadataService } from "./grid.metadata.service";
import { Utilities } from "@tylertech/shared-module";
import { AugmentedModelObject, MetadataKey, RepositoryFormData } from "../../interfaces";
import { ObjectHelpers } from "../../utilities";
import { MetadataService } from "../metadata.service";
import { cellEditValidationKey, serverVerificationKey } from "../metadataErrorService";
import { MetadataPropertyService } from "../metadataPropertyService";
import { MetadataInputTypeHelperService } from "../metadataInputTypeHelperService";
import { ProviderStorageService } from "./provider.storage.service";
import { ResetStateService } from "../reset.state.service";
import { NavigationService } from "@tylertech/core-module";
import { GridLookupService } from "./grid.lookup.service";
import { GridEntityService } from "./grid.entity.service";

interface ActivationRetries
{
    vertical: number;
    horizontal: number;
    navigate: number;
}

//navigateTo can drop its callback, so onDone also fires once this has passed even if no exit path was reached
const activationDoneFallbackMs = 5000;

@Injectable()
export class CellEditService extends ResetStateService
{
    private readonly $metadataService = inject(MetadataService);
    private $providerStorageService = inject(ProviderStorageService);
    private readonly $metadataPropertyService = inject(MetadataPropertyService);
    private $metadataInputTypeHelperService = inject(MetadataInputTypeHelperService);
    private readonly $gridEntityService = inject(GridEntityService);
    private $gridLookupService = inject(GridLookupService);
    private $gridMetadataService = inject(GridMetadataService);


    CellEditCancel: Record<string, boolean> = {};

    CellEditAfterAdd: Record<string, boolean> = {};

    CellEditCell: Record<string, CellType> = {};

    ViewOnly: Record<string, boolean> = {};

    InvalidCells: Record<string, boolean> = {};

    FreeEntryValue: boolean;

    constructor()
    {
        super(inject(NavigationService));
    }

    //onDone runs exactly once, when activation has either entered the cell or given up
    ActivateEditableCell(grid: IgxGridComponent, followingPosition: ICellPosition, enterEditMode: boolean = false, onDone?: () => void): void
    {
        let fallback: ReturnType<typeof setTimeout>;
        let done = (): void =>
        {
            done = () => undefined;
            clearTimeout(fallback);
            onDone?.();
        };
        if (onDone)
            fallback = setTimeout(() => done(), activationDoneFallbackMs);
        //vertical gets extra attempts because the horizontal scroll back to the first column can push the row out of view again
        this.navigateAndActivate(grid, followingPosition, enterEditMode, { vertical: 5, horizontal: 3, navigate: 3 }, () => done());
    }

    //Edit mode must start only after the cell is scrolled into view: entering earlier locks the grid scroll at the old position.
    //Each type of move keeps its own retry count: scrolling back horizontally can re-measure row heights and push the row out of view
    //vertically again, and a shared count lets those horizontal attempts use up the vertical ones before the cell is reached.
    private navigateAndActivate(grid: IgxGridComponent, position: ICellPosition, enterEditMode: boolean, retriesLeft: ActivationRetries, done: () => void): void
    {
        if (position.rowIndex >= grid.dataView.length)
        {
            done();
            return;
        }

        // navigateTo's own vertical scroll waits for the next chunkLoad; if the scroll doesn't change the chunk, the callback is dropped
        // and its pending subscription later fires against stale row indexes. Scroll here with a timeout so navigateTo never has to.
        if (!grid.hasPinnedRecords && grid.navigation.shouldPerformVerticalScroll(position.rowIndex, position.visibleColumnIndex))
        {
            if (retriesLeft.vertical <= 0)
            {
                done();
                return;
            }
            race(grid.verticalScrollContainer.chunkLoad.pipe(take(1)), timer(100))
                .subscribe(() => requestAnimationFrame(() => this.navigateAndActivate(grid, position, enterEditMode, { ...retriesLeft, vertical: retriesLeft.vertical - 1 }, done)));
            grid.verticalScrollContainer.scrollTo(position.rowIndex);
            return;
        }

        //cell is rendered but horizontally off the screen
        if (retriesLeft.horizontal > 0 && grid.navigation.shouldPerformHorizontalScroll(position.visibleColumnIndex, position.rowIndex))
        {
            race(grid.parentVirtDir.chunkLoad.pipe(take(1)), timer(100))
                .subscribe(() => requestAnimationFrame(() => this.navigateAndActivate(grid, position, enterEditMode, { ...retriesLeft, horizontal: retriesLeft.horizontal - 1 }, done)));
            grid.headerContainer.scrollTo(this.getUnpinnedColumnIndex(grid, position.visibleColumnIndex));
            return;
        }

        //cell already rendered and in view, no need to navigateTo
        const renderedCell = this.getRenderedCell(grid, position);
        if (renderedCell)
        {
            this.activateCell(grid, renderedCell, position, enterEditMode);
            done();
            return;
        }
        if (retriesLeft.navigate <= 0)
        {
            done();
            return;
        }

        grid.navigateTo(
            position.rowIndex,
            position.visibleColumnIndex,
            args =>
            {
                //target cell not yet rendered due to scroll virtualization; retry
                if (!args.target)
                {
                    requestAnimationFrame(() => this.navigateAndActivate(grid, position, enterEditMode, { ...retriesLeft, navigate: retriesLeft.navigate - 1 }, done));
                    return;
                }
                this.activateCell(grid, args.target, position, enterEditMode);
                done();
            }
        );
    }

    private getUnpinnedColumnIndex(grid: IgxGridComponent, visibleColumnIndex: number): number
    {
        if (!grid.pinnedColumns?.length)
            return visibleColumnIndex;
        const unpinned = grid.unpinnedColumns.filter(c => !c.columnGroup);
        return unpinned.findIndex(c => c.visibleIndex === visibleColumnIndex);
    }

    private getRenderedCell(grid: IgxGridComponent, position: ICellPosition): any
    {
        const row = grid.rowList?.toArray().find(r => r.index === position.rowIndex);
        return row?.cells?.find(c => c.visibleColumnIndex === position.visibleColumnIndex);
    }

    private activateCell(grid: IgxGridComponent, cell: any, position: ICellPosition, enterEditMode: boolean): void
    {
        cell.activate();
        grid.cdr.detectChanges();
        if (enterEditMode)
            grid.crudService.enterEditMode(grid.getCellByColumn(position.rowIndex, grid.visibleColumns[position.visibleColumnIndex].field));
    }

    VerticalEditNavigationAllowed(keyCode: string, rows: any[], currentRowIndex: number, inputType: string): boolean
    {
        if (this.$metadataInputTypeHelperService.isSelect(inputType) || this.$metadataInputTypeHelperService.isMultiSelect(inputType)
                || this.$metadataInputTypeHelperService.isTypeAhead(inputType) || this.$metadataInputTypeHelperService.isDate(inputType)
                || this.$metadataInputTypeHelperService.isDateTime(inputType) || this.$metadataInputTypeHelperService.isTime(inputType)
                || this.$metadataInputTypeHelperService.isTime24(inputType))
            return false;
        if ((keyCode === "ArrowUp" && currentRowIndex === 0) || (keyCode !== "ArrowUp" && currentRowIndex === (rows.length - 1)))
            return false;
        return true;
    }

    EditNextVerticalCell(
        grid: IgxGridComponent,
        viewMetadata: IGridViewMetadata,
        rowIndex: number,
        columnIndex: number,
        up: boolean
    ): void
    {
        const newRowIndex = up ? rowIndex - 1 : rowIndex + 1;

        if (up && newRowIndex < 0)
            return;
        else if (!up && newRowIndex >= grid.data.length)
            return;

        const field = grid.columns[columnIndex].field;

        if (!this.AllowCellEdit(grid, viewMetadata, columnIndex, field, grid.getCellByColumn(newRowIndex, field), false))
        {
            this.EditNextVerticalCell(grid, viewMetadata, newRowIndex, columnIndex, up);
        }
        else
        {
            const position = <ICellPosition>{ rowIndex: newRowIndex, visibleColumnIndex: grid.columns[columnIndex].visibleIndex };
            this.ActivateEditableCell(grid, position);
        }
    }

    EditFirstCell(
        grid: IgxGridComponent,
        viewMetadata: IGridViewMetadata,
        _rowIndex?: number,
        enterEditMode: boolean = false
    ): void
    {
        const rowIndex = !Utilities.IsNullOrUndefined(_rowIndex)
            ? _rowIndex
            : viewMetadata.GridProperties.AddToTop || grid.data.length === 0
                ? 0
                : grid.data.length - 1;
        const position = this.GetFirstEditableCell(grid, viewMetadata, rowIndex, 0);
        if (Utilities.IsNullOrUndefined(position))
            return;
        this.ActivateEditableCell(grid, position, enterEditMode);
    }


    AllowCellEdit(grid: IgxGridComponent, viewMetadata: IGridViewMetadata, currentColumnIndex: number, columnName: string, cell?: CellType, tryEditNext: boolean = true): boolean
    {
        const providerId = viewMetadata?.GridProperties["$providerId"];
        if (!this.AllowCellEditType(columnName, viewMetadata))
            return false;
        const index = cell ? cell.row?.index : this.CellEditCell[providerId]?.row?.index;
        if (Utilities.IsNullOrUndefined(index))
            return true;
        if (Utilities.IsNullOrUndefined(this.ViewOnly[columnName]) || !Utilities.IsNullOrUndefined(cell) || !viewMetadata.GridProperties.CanEdit)
        {
            const cell = grid.getCellByColumn(index, columnName);
            const columns = viewMetadata["$columns"];
            const viewOnly = this.ViewOnlyCell(
                cell,
                grid,
                this.$providerStorageService.GridData[providerId],
                viewMetadata,
                columns
            );
            if (Utilities.IsNullOrUndefined(currentColumnIndex))
                return false;
            if (tryEditNext && viewOnly && !this.ViewOnly[columnName] && viewMetadata.GridProperties.CanEdit && grid.columns[currentColumnIndex].field === columnName)
                this.ActivateEditableCell(grid, this.GetNextEditableCellSafe(grid, viewMetadata, currentColumnIndex, index, grid.columns[currentColumnIndex].visibleIndex));
            this.ViewOnly[columnName] = viewOnly;
        }
        return !this.ViewOnly[columnName];
    }

    IsAfterLastEditableColumn(grid: IgxGridComponent, viewMetadata: IGridViewMetadata, rowIndex: number, columnIndex: number): boolean
    {
        const field = grid.visibleColumns[columnIndex].field;
        const cell = grid.getCellByColumn(rowIndex, field);
        const editableColumns = grid.visibleColumns.filter(x => this.AllowCellEdit(grid, viewMetadata, columnIndex, x.field, cell, false));
        if (editableColumns.length === 0)
            return false;
        const lastEditableColumnName = editableColumns[editableColumns.length - 1].field;
        const lastEditableColumnCell = grid.getCellByColumn(rowIndex, lastEditableColumnName).column;

        return lastEditableColumnCell.visibleIndex <= columnIndex;
    }

    GetNextEditableCellSafe(grid: IgxGridComponent, viewMetadata: IGridViewMetadata, currentColumnIndex: number, rowIndex: number, columnIndex: number): ICellPosition
    {
        const isAtLastColumn = this.IsAfterLastEditableColumn(grid, viewMetadata, rowIndex, columnIndex);
        const isLastRow = rowIndex === grid.filteredSortedData.length - 1;
        let rowIndexForPermission = rowIndex;
        if (isAtLastColumn && !isLastRow)
            rowIndexForPermission = rowIndex + 1;
        if (isAtLastColumn && isLastRow)
            return null;
        return this.GetNextEditableCell(grid, viewMetadata, currentColumnIndex, rowIndex, columnIndex, rowIndexForPermission);
    }

    GetPreviousEditableCell(grid: IgxGridComponent, viewMetadata: IGridViewMetadata, currentColumnIndex: number, rowIndex: number, columnIndex: number, rowIndexForPermission: number): ICellPosition
    {
        return grid.getPreviousCell(
            rowIndex,
            columnIndex,
            (column: IgxColumnComponent) => this.AllowCellEdit(grid, viewMetadata, currentColumnIndex, column.field, grid.getCellByColumn(rowIndexForPermission, column.field), false)
        );
    }

    GetNextEditableCell(grid: IgxGridComponent, viewMetadata: IGridViewMetadata, currentColumnIndex: number, rowIndex: number, columnIndex: number, rowIndexForPermission: number): ICellPosition
    {
        return grid.getNextCell(
            rowIndex,
            columnIndex,
            (column: IgxColumnComponent) => this.AllowCellEdit(grid, viewMetadata, currentColumnIndex, column.field, grid.getCellByColumn(rowIndexForPermission, column.field), false)
        );
    }

    GetFirstEditableCell(grid: IgxGridComponent, viewMetadata: IGridViewMetadata, rowIndex: number, columnIndex: number = 0): ICellPosition
    {
        //TODO: If we get to last column and no editable cells, should we move down a row and keep trying or end it?
        const column = grid.getColumnByVisibleIndex(columnIndex);
        if (!column)
            return null;
        const cell = grid.getCellByColumn(rowIndex, column.field);
        if (!cell)
            return null;
        if (!this.AllowCellEdit(grid, viewMetadata, columnIndex, column.field, cell, false))
        {
            return this.GetFirstEditableCell(
                grid,
                viewMetadata,
                rowIndex,
                columnIndex + 1
            );
        }
        return <ICellPosition>{ rowIndex: rowIndex, visibleColumnIndex: columnIndex };
    }

    IsLastEditableColumn(grid: IgxGridComponent, viewMetadata: IGridViewMetadata, rowIndex: number, columnIndex: number): boolean
    {
        const field = grid.visibleColumns[columnIndex].field;
        const cell = grid.getCellByColumn(rowIndex, field);
        const editableColumns = grid.visibleColumns.filter(x => this.AllowCellEdit(grid, viewMetadata, columnIndex, x.field, cell, false));
        if (editableColumns.length === 0)
            return false;
        return field === editableColumns[editableColumns.length - 1].field;
    }

    ViewOnlyCell(
        cell: CellType,
        grid: IgxGridComponent,
        rows: any[],
        viewMetadata: IGridViewMetadata,
        columns: any[]
    ): boolean
    {
        if (!cell) return true;
        if (!rows) return false;

        if (!this.isAllowedCellEditType(this.$gridMetadataService.getProperty(viewMetadata.GridProperties.ClassMetadata, cell.column.field)?.InputType))
            return true;
        const row = rows.find(x => x.Id === cell.row.key);
        if (Utilities.IsNullOrUndefined(row))
            return false;
        if (columns.filter(x => x.Name === viewMetadata.GridProperties.CanEditProperty).length === 1)
        {
            if (row[columns.filter(x => x.Name === viewMetadata.GridProperties.CanEditProperty)[0].Name]?.toString().toLowerCase() !== "true")
                return true;
        }
        const model = row["$$model"];

        if (!viewMetadata.GridProperties.CanEdit && !model["$addedCellEdit"])
            return true;

        const flattenedProperties = this.$metadataService.FlattenProperties(
            viewMetadata.GridProperties.ClassMetadata,
            model.$$Parent,
            model
        );
        const property = grid.columns.find(x => x.index === cell.id.columnID).field;
        const propertyMetadata = flattenedProperties.find(x => x.name === property).property;
        return this.$metadataPropertyService.isViewOnly(propertyMetadata, model, model["$$Parent"]);
    }

    AllowCellEditType(columnName: string, viewMetadata: IGridViewMetadata): boolean
    {
        return (
            !!viewMetadata &&
            !viewMetadata.ViewOnly &&
            viewMetadata.EditType === GridEditType.Cell &&
            (viewMetadata.GridProperties.CanEdit || viewMetadata.GridProperties.CanAdd) &&
            this.isAllowedCellEditType(
                this.$gridMetadataService.getProperty(viewMetadata.GridProperties.ClassMetadata, columnName)?.InputType
            )
        );
    }

    private isAllowedCellEditType(type: string): boolean
    {
        return (
            this.$metadataInputTypeHelperService.isButton(type) ||
            this.$metadataInputTypeHelperService.isText(type) ||
            this.$metadataInputTypeHelperService.isTextArea(type) ||
            this.$metadataInputTypeHelperService.isSelect(type) ||
            this.$metadataInputTypeHelperService.isMultiSelect(type) ||
            this.$metadataInputTypeHelperService.isDate(type) ||
            this.$metadataInputTypeHelperService.isTime(type) ||
            this.$metadataInputTypeHelperService.isTime24(type) ||
            this.$metadataInputTypeHelperService.isDateTime(type) ||
            this.$metadataInputTypeHelperService.isCheck(type) ||
            this.$metadataInputTypeHelperService.isNumber(type) ||
            this.$metadataInputTypeHelperService.isNumberWithoutDisplayFiltering(type) ||
            this.$metadataInputTypeHelperService.isCurrency(type) ||
            this.$metadataInputTypeHelperService.isPercent(type) ||
            this.$metadataInputTypeHelperService.isPhoneNumber(type) ||
            this.$metadataInputTypeHelperService.isPhoneNumberWithExtension(type) ||
            this.$metadataInputTypeHelperService.isTypeAhead(type)
        );
    }

    RecalculateAfterCellEdit(model: any, classMetadata: ClassMetadata, property: string, originalModelValue: any, force: boolean, formData: RepositoryFormData): void
    {
        const newValue = model[property];

        const propertyMetadata = classMetadata.Properties.find(p => p.Name === property);
        if (propertyMetadata.RecalculateOnChange &&
            (force || this.$metadataInputTypeHelperService.delayRecalculateForCellEdit(propertyMetadata.InputType))
        )
        {
            model[property] = originalModelValue;
            this.$metadataPropertyService.recalculateProperties(
                classMetadata.TypeName,
                propertyMetadata,
                model,
                model.$$Parent,
                newValue,
                undefined,
                false,
                false,
                formData
            );
            model[property] = newValue;
        }
    }

    AfterCellEdit(
        cell: CellType,
        row: any,
        propertyMetadata: PropertyMetadata,
        viewMetadata: IGridViewMetadata,
        grid: IgxGridComponent,
        providerId: string,
        newModelValue: any
    ): void
    {
        const self = this;
        row["$$model"][propertyMetadata.Name] = newModelValue;
        this.$gridEntityService.fireModelChangedListeners(providerId);
        row[propertyMetadata.Name] = newModelValue;
        if (Utilities.IsNullOrUndefined(row[propertyMetadata.Name]))
            updateGridCell();
        else if (!propertyMetadata.LookupProperty && propertyMetadata.Lookup && self.$metadataInputTypeHelperService.isLookup(propertyMetadata.InputType))
            updateFromLookup();
        else if (propertyMetadata.LookupProperty && self.$metadataInputTypeHelperService.isLookup(propertyMetadata.InputType))
        {
            let ids: string[] = [];
            if (self.$metadataInputTypeHelperService.isMultiSelect(propertyMetadata.InputType))
                ids = row[propertyMetadata.Name].toString().split(",");
            else
                ids.push(row[propertyMetadata.Name].toString());
            const list: KeyValuePair<MetadataKey, string>[] = self.$metadataService.DependentModuleValue(
                propertyMetadata.LookupProperty,
                row["$$model"]["$$Parent"],
                row["$$model"]
            );
            if (Utilities.IsNullOrUndefinedOrEmpty(list))
            {
                if (propertyMetadata.Lookup)
                    updateFromLookup();
                return;
            }

            const values = [];
            ids.forEach(id =>
            {
                values.push(list.find(x => x.Key.toString() === id.toString())?.Value);
            });
            const val =  Utilities.IsNullOrUndefinedOrEmpty(values) ? "" : values.join(",");
            if (Utilities.IsNullOrUndefinedOrEmpty(val) && propertyMetadata.Lookup)
            {
                updateFromLookup();
                return;
            }
            row[propertyMetadata.Name] = val;
            updateGridCell();
        }
        else
        {
            updateGridCell();
        }

        function updateFromLookup(): void
        {
            if (propertyMetadata.FreeEntry && self.FreeEntryValue)
            {
                self.FreeEntryValue = false;
                updateGridCell();
            }
            else
            {
                let ids: string[] = [];
                if (self.$metadataInputTypeHelperService.isMultiSelect(propertyMetadata.InputType))
                    ids = row[propertyMetadata.Name].toString().split(",");
                else
                    ids.push(row[propertyMetadata.Name].toString());
                self.$gridLookupService
                    .LookupValueForTable(
                        propertyMetadata,
                        self.$providerStorageService.GridData[providerId],
                        ids
                    )
                    .then((x) =>
                    {
                        updateGridCell();
                    });
            }
        }

        function updateGridCell(): void
        {
            if (!cell)
                return;
            cell.editValue = row[propertyMetadata.Name];
            if (propertyMetadata.InputType === "PhoneNumber" || propertyMetadata.InputType === "Number")
                //Phone stores as unformatted
                row[propertyMetadata.Name] = self.$metadataService.getFormattedValueFromProp(
                    newModelValue,
                    propertyMetadata
                );
            grid.cdr.detectChanges();
        }
    }

    SetCellEditValidity(providerId: string, model: AugmentedModelObject, property: string, isInvalid: boolean): void
    {
        if (!model["$cellEditInvalidProperties"])
            model["$cellEditInvalidProperties"] = [];
        const index = model["$cellEditInvalidProperties"].indexOf(property);
        if (isInvalid)
        {
            if (index === -1)
                model["$cellEditInvalidProperties"].push(property);
        }
        else
        {
            if (index > -1)
                model["$cellEditInvalidProperties"].splice(index, 1);
        }
        this.InvalidCells[providerId] = model["$cellEditInvalidProperties"].length > 0;
    }

    SubPropertyIsOwnedByParent(formEntity: AugmentedModelObject, cellEditModel: AugmentedModelObject<unknown, AugmentedModelObject>, subPropertyName: string): boolean
    {
        if (Utilities.IsNullOrUndefined(subPropertyName))
            return false;
        if (!Utilities.IsNullOrUndefined(formEntity?.[subPropertyName]))
            return false;
        return !Utilities.IsNullOrUndefined(cellEditModel?.$$Parent?.[subPropertyName]);
    }

    ClearCellEditErrors(model: AugmentedModelObject, properties: PropertyMetadata[]): void
    {
        this.SetCellEditErrors(model, properties, true);
    }

    SetCellEditErrors(model: AugmentedModelObject, properties: PropertyMetadata[], clear: boolean = false): void
    {
        let modelToUse: AugmentedModelObject = model;
        if (Utilities.IsNullOrUndefined(model["$cellEditInvalidProperties"]) && model.$$Parent)
            modelToUse = model.$$Parent;
        if (Utilities.IsNullOrUndefined(modelToUse["$cellEditInvalidProperties"]))
            return;
        if (!modelToUse.$errors)
            modelToUse.$errors = {};

        Object.keys(modelToUse.$errors).forEach(propertyName => removeCellEditError(propertyName));
        if (clear)
            return;

        modelToUse["$cellEditInvalidProperties"].forEach(propertyName =>
        {
            if (!Utilities.IsNullOrUndefinedOrEmpty(modelToUse.$errors[propertyName]?.[serverVerificationKey]))
                return;
            const property = properties.find(p => p.Name === propertyName);
            const label = property ? property.Label : propertyName;
            ObjectHelpers.safeAccess(modelToUse.$errors, propertyName, {})[cellEditValidationKey] = `${label} is invalid`;
        });

        function removeCellEditError(propertyName: string): void
        {
            const fieldErrors = modelToUse.$errors[propertyName];
            if (!fieldErrors)
                return;
            delete fieldErrors[cellEditValidationKey];
            if (Utilities.IsNullOrUndefinedOrEmptyObject(fieldErrors))
                delete modelToUse.$errors[propertyName];
        }
    }
}
